import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Phone, Mail, Heart, Users, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo, services, officeImages, heroImages } from '../data/mock';
import SEO from '../components/SEO';

const Home = () => {
  const featuredServices = services.slice(0, 3);

  const getServiceIcon = (iconName) => {
    const icons = {
      User: <Heart className="h-8 w-8" />,
      Heart: <Heart className="h-8 w-8" />,
      Users: <Users className="h-8 w-8" />,
      Sparkles: <Sparkles className="h-8 w-8" />,
    };
    return icons[iconName] || <Heart className="h-8 w-8" />;
  };

  return (
    <>
      <SEO 
        title="Marriage & Family Therapy in Lebanon, PA"
        description="Calling The Deep Counseling Services provides compassionate therapy for individuals, couples, and families in Lebanon, PA. Rhonda Zook, RN, MAMFT offers evidence-based counseling to help you discover your inner strength."
        keywords="marriage therapy Lebanon PA, family therapy, couples counseling, individual therapy, MAMFT therapist, Lebanon Pennsylvania counselor, relationship therapy, premarital counseling"
        url="/"
      />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImages.home})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div className="text-center mb-4">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2">
                Calling The Deep Counseling Services
              </h2>
            </div>
            
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 backdrop-blur-sm rounded-full text-teal-300 text-sm font-medium">
                <Heart className="h-4 w-4" />
                Marriage & Family Therapy
              </div>
            </div>
            
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-6">
              Discover Your
              <span className="block text-teal-400">Inner Strength</span>
            </h1>
            
            <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-xl">
              At Calling The Deep Counseling Services, we provide a safe, compassionate space for individuals, couples, and families to explore, heal, and grow together.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-6 text-lg">
                  Start Your Journey
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Explore Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">Welcome</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mt-2 mb-6">
                A Safe Space for Healing & Growth
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Life can present challenges that feel overwhelming. Whether you're struggling with relationship issues, navigating family dynamics, or seeking personal growth, you don't have to face it alone.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                At Calling The Deep, we believe that within each person lies the capacity for profound healing and transformation. Our therapeutic approach creates a nurturing environment where you can explore your deepest concerns and discover new pathways forward.
              </p>
              <Link to="/about">
                <Button variant="outline" className="border-teal-600 text-teal-600 hover:bg-teal-50">
                  Learn More About Me
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {officeImages.slice(0, 2).map((image, index) => (
                <div 
                  key={image.id}
                  className={`rounded-2xl overflow-hidden shadow-lg ${index === 0 ? 'col-span-2' : ''}`}
                >
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">Our Services</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mt-2 mb-4">
              How We Can Help
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We offer a range of therapeutic services tailored to meet you where you are in your journey.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuredServices.map((service) => (
              <Card 
                key={service.id}
                className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white overflow-hidden"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center text-teal-600 mb-6 group-hover:bg-teal-500 group-hover:text-white transition-colors duration-300">
                    {getServiceIcon(service.icon)}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    {service.shortDescription}
                  </p>
                  <Link 
                    to="/services"
                    className="inline-flex items-center text-teal-600 font-medium hover:text-teal-700 transition-colors"
                  >
                    Learn More
                    <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white">
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Location & Hours Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">Visit Us</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mt-2 mb-4">
              Location & Office Hours
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Card */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-500 to-teal-600 text-white">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <a href={`tel:${businessInfo.phone}`} className="flex items-start gap-4 hover:opacity-80 transition-opacity">
                    <Phone className="h-5 w-5 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Phone</p>
                      <p className="text-teal-100">{businessInfo.phone}</p>
                    </div>
                  </a>
                  <a href={`mailto:${businessInfo.email}`} className="flex items-start gap-4 hover:opacity-80 transition-opacity">
                    <Mail className="h-5 w-5 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Email</p>
                      <p className="text-teal-100">{businessInfo.email}</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-4">
                    <MapPin className="h-5 w-5 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Address</p>
                      <p className="text-teal-100">
                        Wallace House Suite C<br />
                        336 Cumberland Street<br />
                        {businessInfo.address.city}, {businessInfo.address.state} {businessInfo.address.zip}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Office Hours Card */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Clock className="h-6 w-6 text-teal-600" />
                  <h3 className="text-xl font-semibold text-slate-800">Office Hours</h3>
                </div>
                <div className="text-center py-4">
                  <p className="text-2xl font-semibold text-slate-800 mb-2">By Appointment Only</p>
                  <p className="text-slate-600">Contact us to schedule your session at a time that works best for you.</p>
                </div>
              </CardContent>
            </Card>

            {/* Office Image */}
            <Card className="border-0 shadow-lg overflow-hidden">
              <img
                src={officeImages[2]?.url || officeImages[0].url}
                alt="Our Office"
                className="w-full h-full object-cover min-h-[300px]"
              />
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Taking the first step toward healing can be challenging. We're here to support you every step of the way. Reach out today to schedule your consultation.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-6 text-lg">
              Schedule Your Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;
